import { css } from 'lit';

// Figma: 3form Marketing, "OPTION A" (node 25872:59077, digital print) and
// "OPTION A2" (node 25823:83354, etched glass).
export const styles = css`
  :host {
    --designer-section-gap: calc(24rem / var(--default-rem-px-count));
    --designer-label-gap: calc(10rem / var(--default-rem-px-count));
    --designer-control-height: calc(53rem / var(--default-rem-px-count));
    /* Every toggle group is this wide with equal segments; only the
       opacity row spans the column. */
    --designer-group-width: calc(268rem / var(--default-rem-px-count));
    --designer-control-border: #c7c7c7;
    --designer-control-muted: #666666;
    --designer-panel-border: var(--block-rule-color, #c0c0c0);
    --designer-selected-bg: var(--block-text-color, #222222);
    --designer-selected-fg: var(--block-bg-color, #ffffff);
  }

  /* Always open: the page has one barrel and it is the page's purpose. */
  :host(:not(:last-child)) > .barrel-wrapper {
    padding-bottom: calc(40rem / var(--default-rem-px-count));
  }

  .barrel-wrapper {
    display: flex;
    flex-direction: column;
  }

  .barrel-header {
    display: flex;
    align-items: center;
    min-height: calc(60rem / var(--default-rem-px-count));
    border-top: 2px solid var(--block-text-color, #222222);
  }

  .barrel-heavy-text {
    font-weight: var(--fw-medium);
  }

  .sections {
    display: flex;
    flex-direction: column;
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: var(--designer-label-gap);
    padding: var(--designer-section-gap) 0;
    border-bottom: 1px solid var(--designer-panel-border);
  }

  .section:first-child {
    padding-top: 0;
  }

  .section-label {
    font-size: var(--fs-label-small);
    line-height: var(--lh-label-small);
    letter-spacing: -0.21px;
    font-weight: var(--fw-medium);
  }

  /* Labels span the control they describe, not the column. */
  .range-group {
    display: flex;
    flex-direction: column;
    gap: var(--designer-label-gap);
    width: var(--designer-group-width);
    max-width: 100%;
  }

  .range-labels {
    display: flex;
    justify-content: space-between;
    font-size: calc(12rem / var(--default-rem-px-count));
    line-height: calc(13rem / var(--default-rem-px-count));
    letter-spacing: -0.18px;
  }

  /* Segmented toggles: adjacent borders overlap by 1px as in the design. */
  .segments {
    display: flex;
    width: var(--designer-group-width);
    max-width: 100%;
  }

  .segments.is-full {
    width: 100%;
  }

  .segments > * {
    flex: 1 1 0;
    min-width: 0;
    margin-right: -1px;
  }

  .segments > *:last-child {
    margin-right: 0;
  }

  .segment {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: var(--designer-control-height);
    padding: calc(4rem / var(--default-rem-px-count))
      calc(8rem / var(--default-rem-px-count));
    border: 1px solid var(--designer-control-border);
    background: transparent;
    color: var(--designer-control-muted);
    font-family: inherit;
    font-size: var(--fs-label-small);
    line-height: calc(20rem / var(--default-rem-px-count));
    font-weight: var(--fw-regular);
    white-space: nowrap;
    cursor: pointer;
  }

  .segment.is-selected {
    background: var(--designer-selected-bg);
    border-color: var(--designer-selected-bg);
    color: var(--designer-selected-fg);
    font-weight: var(--fw-medium);
    z-index: 1;
  }

  .segment:focus-visible {
    outline: 2px solid var(--block-text-color, #222222);
    outline-offset: 2px;
    z-index: 2;
  }

  /* Opacity levels are the short row in the design. */
  .segments.is-full .segment {
    min-height: calc(37rem / var(--default-rem-px-count));
  }

  .picker {
    display: flex;
    flex-direction: column;
    gap: calc(16rem / var(--default-rem-px-count));
  }

  .hex-fields {
    display: flex;
    gap: calc(8rem / var(--default-rem-px-count));
    margin-top: calc(-8rem / var(--default-rem-px-count));
  }

  .hex-fields__format,
  .hex-fields__value {
    display: flex;
    align-items: center;
    gap: calc(4rem / var(--default-rem-px-count));
    min-height: calc(28rem / var(--default-rem-px-count));
    padding: calc(4rem / var(--default-rem-px-count))
      calc(6rem / var(--default-rem-px-count));
    border: 1px solid var(--designer-panel-border);
    background: var(--block-bg-color, #ffffff);
    font-size: var(--fs-body-small);
    line-height: calc(20rem / var(--default-rem-px-count));
  }

  .hex-fields__format {
    color: var(--designer-control-muted);
  }

  .hex-fields__value {
    flex: 1 1 0;
    min-width: 0;
  }

  .hex-fields__hash {
    color: #9ca3af;
  }

  .hex-fields__value input {
    flex: 1 1 0;
    min-width: 0;
    border: 0;
    padding: 0;
    background: transparent;
    font: inherit;
    color: inherit;
    text-transform: uppercase;
    outline: none;
  }

  .hex-fields__value:focus-within {
    border-color: var(--block-text-color, #222222);
  }

  .opacity-levels {
    display: flex;
    flex-direction: column;
    gap: var(--designer-label-gap);
  }

  .rep-note {
    padding-top: calc(30rem / var(--default-rem-px-count));
    font-size: var(--fs-body);
    line-height: var(--lh-body);
    letter-spacing: var(--ls-body);
  }

  .cta-row {
    display: flex;
    flex-wrap: wrap;
    gap: calc(20rem / var(--default-rem-px-count))
      calc(40rem / var(--default-rem-px-count));
    padding-top: calc(30rem / var(--default-rem-px-count));
  }

  .cta-row cta-button {
    display: block;
  }

  .download-links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: calc(16rem / var(--default-rem-px-count));
    padding-top: calc(30rem / var(--default-rem-px-count));
  }

  /* Matches cta-text's download link, which cannot be used here because
     its click handler is a navigation. */
  .download-link {
    display: inline-flex;
    align-items: center;
    gap: calc(8rem / var(--default-rem-px-count));
    color: inherit;
    font-size: var(--fs-cta);
    line-height: var(--lh-cta);
    letter-spacing: var(--ls-cta);
    font-weight: var(--fw-medium);
    text-decoration: none;
    white-space: nowrap;
  }

  .download-link__icon svg {
    display: block;
    width: calc(10rem / var(--default-rem-px-count));
    height: calc(10rem / var(--default-rem-px-count));
  }

  .download-link__text {
    position: relative;
  }

  .download-link__text::after {
    content: '';
    position: absolute;
    bottom: -0.15em;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: currentColor;
    transform: scaleX(0);
    transform-origin: right center;
    transition: transform 0.25s ease-in;
  }

  .download-link:hover .download-link__text::after,
  .download-link:focus-visible .download-link__text::after {
    transform: scaleX(1);
    transform-origin: left center;
  }

  .download-link[aria-busy='true'] {
    cursor: progress;
  }

  .download-link:focus-visible {
    outline: 2px solid var(--block-text-color, #222222);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    .download-link__text::after {
      transition: none;
    }
  }

  @media print {
    .barrel-header {
      border-top: 1px solid #222222;
    }
  }
`;
